/**
 * Pure deep-link dispatcher per `architecture.md` §3.3.
 *
 * Implements the §3.3 dispatch table for the URL grammar in
 * `navigation-map.md` §5:
 *
 *   #view=<base64url>           → useViewerStore.hydrateFromUrlState (RW)
 *   #viewro=<base64url>         → useViewerStore.hydrateFromUrlState (RO)
 *   #mode=<map|3d|...>          → useViewerStore.setMode
 *   ?layer=<stac-item-id>       → useViewerStore.addByStacId? else announce
 *   #copilot=<sessionId>:<idx>  → drawerHost.open('copilot') + scroll target
 *   #audit=<rowId>              → drawerHost.open('audit')   + scroll target
 *
 * The dispatcher is pure: callers inject side-effect deps so unit tests
 * can spy on each effect without mounting React. `applyDeepLink` is
 * idempotent — calling it twice with the same `(hash, search)` pair
 * MUST be a no-op for state mutations beyond the first call. The
 * `<DeepLinkDispatcher />` React adapter keeps the last-applied key in
 * a ref to enforce this invariant on every `location.key` change.
 *
 * Cite:
 *   - dispatch `WP-E1-routes-foundation-1.md` §4 step 4 + AC5.
 *   - `architecture.md` §3.3 (deep-link grammar plumbing).
 *   - `navigation-map.md` §5 (URL grammar).
 *   - `state/url/decode.ts` (canonical decoder for `#view=` / `#viewro=`).
 */

import { decodeView, isFailure } from '@/state/url';
import {
  HASH_PREFIX_EDITABLE,
  HASH_PREFIX_READONLY,
  type ViewerState as UrlViewerState,
} from '@/state/url/schema';
import type { WorkspaceMode } from '@/state/useViewerStore';

const VALID_MODES: ReadonlyArray<WorkspaceMode> = [
  'map',
  '3d',
  'profile',
  'pointcloud',
  'compare',
];

const HASH_PREFIX_MODE = '#mode=';
const HASH_PREFIX_COPILOT = '#copilot=';
const HASH_PREFIX_AUDIT = '#audit=';
const HASH_PREFIX_PRESET = 'preset=';

export interface DeepLinkDeps {
  /** Hydrate the viewer store from a decoded URL-state payload. */
  hydrateFromUrlState: (payload: UrlViewerState, opts: { readOnly: boolean }) => void;
  /** Set the workspace mode (drives the active pane). */
  setMode: (mode: WorkspaceMode) => void;
  /**
   * Cold-bookmark a layer by STAC item id. Optional because not every
   * deployment context will have the catalog adapter wired. When
   * absent, the dispatcher announces a polite status message instead
   * of silently dropping the request (per dispatch §4 step 4 bullet 4).
   */
  addByStacId?: (stacItemId: string) => void;
  /**
   * Open the singleton drawer host. The dispatcher always opens the
   * drawer for `#copilot=` / `#audit=` even when no scroll-target
   * setter is wired yet — per dispatch §4 step 4 bullets 5 & 6
   * "ALWAYS open the drawer".
   */
  drawerHostOpen: (id: 'copilot' | 'audit', onClose: () => void) => void;
  /** Live-region announcer for fallback narration. */
  liveRegionAnnounce: (role: 'status' | 'alert', text: string) => void;
  /** Optional copilot scroll-target hook (added by WP-E11). */
  setCopilotScrollTarget?: (sessionId: string, messageIdx: number) => void;
  /**
   * Optional audit scroll-target hook (added by WP-E8). */
  setAuditScrollTarget?: (rowId: string) => void;
  /**
   * Optional handler for `#preset=<id>` (added by WP-PP5 / RFC-108.1).
   * When present, called with the preset id extracted from the hash.
   */
  setActivePreset?: (presetId: string) => void;
  /** Sink for non-fatal warnings (defaults to console.warn). */
  warn?: (message: string) => void;
}

export interface DeepLinkResult {
  /** Hash dispatch outcome. `none` when the hash had no recognised prefix. */
  hash: 'view' | 'viewro' | 'mode' | 'copilot' | 'audit' | 'preset' | 'unknown' | 'none';
  /** Whether `?layer=` was processed. */
  query: 'layer' | 'none';
  /** Whether any state mutation actually fired. */
  mutated: boolean;
  /** Per-effect notes (useful for tests + telemetry). */
  notes: string[];
}

function parseModeFragment(hash: string): WorkspaceMode | null {
  if (!hash.startsWith(HASH_PREFIX_MODE)) return null;
  const value = hash.slice(HASH_PREFIX_MODE.length);
  return (VALID_MODES as ReadonlyArray<string>).includes(value)
    ? (value as WorkspaceMode)
    : null;
}

function parseCopilotFragment(
  hash: string,
): { sessionId: string; messageIdx: number } | null {
  if (!hash.startsWith(HASH_PREFIX_COPILOT)) return null;
  const value = hash.slice(HASH_PREFIX_COPILOT.length);
  const colon = value.indexOf(':');
  if (colon <= 0 || colon >= value.length - 1) return null;
  const sessionId = value.slice(0, colon);
  const idxRaw = value.slice(colon + 1);
  const messageIdx = Number.parseInt(idxRaw, 10);
  if (!Number.isFinite(messageIdx) || messageIdx < 0) return null;
  return { sessionId, messageIdx };
}

function parseAuditFragment(hash: string): string | null {
  if (!hash.startsWith(HASH_PREFIX_AUDIT)) return null;
  const value = hash.slice(HASH_PREFIX_AUDIT.length).trim();
  return value.length > 0 ? value : null;
}

/**
 * Parse `#preset=<id>` from a hash string.
 * The grammar allows stacking: `#viewro=...&preset=<id>`. The hash may
 * contain multiple `&`-separated key=value pairs; we scan all parts for
 * the `preset=` key so it works whether it comes first or second.
 */
function parsePresetFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of raw.split('&')) {
    if (part.startsWith(HASH_PREFIX_PRESET)) {
      const id = part.slice(HASH_PREFIX_PRESET.length).trim();
      return id.length > 0 ? id : null;
    }
  }
  return null;
}

/**
 * Apply a `(hash, search)` pair to the dispatcher deps. Pure and
 * idempotent at the state-mutation layer — callers must guard against
 * double-invocation themselves if they want the announcement / drawer
 * effects to fire only once per change.
 */
export function applyDeepLink(
  hash: string,
  search: URLSearchParams,
  deps: DeepLinkDeps,
): DeepLinkResult {
  const result: DeepLinkResult = {
    hash: 'none',
    query: 'none',
    mutated: false,
    notes: [],
  };
  const warn = deps.warn ?? ((message: string) => console.warn(`[deep-link] ${message}`));

  // -----------------------------------------------------------------
  // ?layer=<stac-item-id> — cold-bookmark a single layer.
  // -----------------------------------------------------------------
  const layerId = search.get('layer');
  if (layerId !== null && layerId.length > 0) {
    result.query = 'layer';
    if (deps.addByStacId) {
      deps.addByStacId(layerId);
      result.mutated = true;
      result.notes.push(`addByStacId(${layerId})`);
    } else {
      warn(`Bookmark layer add not yet wired (WP-E3): layer=${layerId}`);
      deps.liveRegionAnnounce(
        'status',
        'Bookmark layer add not yet wired (WP-E3)',
      );
      result.notes.push(`addByStacId-missing(${layerId})`);
    }
  }

  // -----------------------------------------------------------------
  // Hash dispatch — exactly one prefix may match.
  // -----------------------------------------------------------------
  if (hash.length === 0 || hash === '#') {
    return result;
  }

  if (hash.startsWith(HASH_PREFIX_READONLY)) {
    const decoded = decodeView(hash);
    if (isFailure(decoded)) {
      result.hash = 'viewro';
      result.notes.push(`viewro-decode-failed:${decoded.reason}`);
      warn(`#viewro= decode failed: ${decoded.reason}`);
      return result;
    }
    deps.hydrateFromUrlState(decoded.state, { readOnly: true });
    result.hash = 'viewro';
    result.mutated = true;
    result.notes.push('viewro-hydrated');
    return result;
  }

  if (hash.startsWith(HASH_PREFIX_EDITABLE)) {
    const decoded = decodeView(hash);
    if (isFailure(decoded)) {
      result.hash = 'view';
      result.notes.push(`view-decode-failed:${decoded.reason}`);
      warn(`#view= decode failed: ${decoded.reason}`);
      return result;
    }
    deps.hydrateFromUrlState(decoded.state, { readOnly: false });
    result.hash = 'view';
    result.mutated = true;
    result.notes.push('view-hydrated');
    return result;
  }

  const mode = parseModeFragment(hash);
  if (mode !== null) {
    deps.setMode(mode);
    result.hash = 'mode';
    result.mutated = true;
    result.notes.push(`setMode(${mode})`);
    return result;
  }

  const copilot = parseCopilotFragment(hash);
  if (copilot !== null) {
    deps.drawerHostOpen('copilot', () => {});
    if (deps.setCopilotScrollTarget) {
      deps.setCopilotScrollTarget(copilot.sessionId, copilot.messageIdx);
    } else {
      result.notes.push('copilot-scroll-target-missing(WP-E11)');
    }
    result.hash = 'copilot';
    result.mutated = true;
    result.notes.push(`copilot(${copilot.sessionId}:${copilot.messageIdx})`);
    return result;
  }

  const auditRow = parseAuditFragment(hash);
  if (auditRow !== null) {
    deps.drawerHostOpen('audit', () => {});
    if (deps.setAuditScrollTarget) {
      deps.setAuditScrollTarget(auditRow);
    } else {
      result.notes.push('audit-scroll-target-missing(WP-E8)');
    }
    result.hash = 'audit';
    result.mutated = true;
    result.notes.push(`audit(${auditRow})`);
    return result;
  }

  // #preset=<id> — may appear standalone OR stacked with #viewro= / #view=.
  // When stacked the view/viewro handler already returned above; here we
  // handle the standalone case (and the stacked case after a bare hash).
  const presetId = parsePresetFragment(hash);
  if (presetId !== null) {
    if (deps.setActivePreset) {
      deps.setActivePreset(presetId);
      result.hash = 'preset';
      result.mutated = true;
      result.notes.push(`setActivePreset(${presetId})`);
    } else {
      result.hash = 'preset';
      result.notes.push(`preset-handler-missing:${presetId}`);
      warn(`#preset= handler not wired (WP-PP5): preset=${presetId}`);
    }
    return result;
  }

  result.hash = 'unknown';
  result.notes.push(`unknown-hash:${hash.slice(0, 12)}`);
  return result;
}
